import { Card, Select, Space, Steps, Typography } from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { useHistory } from 'react-router-dom';

import PPPageHeader from '../../components/pageHeader/PPPageHeader';
import SectionCard from '../../components/sectionCard/SectionCard';
import useStore from '../../store/useStore';
import { getObjectFromArray, imageUpload } from '../../utils';
import CitizenForm from './CitizenForm';
import OrganizationForm from './OrganizationForm';
import { RegisterProvider } from './RegisterProviderContext';
import Step1 from './Steps/Step1';
import Step2 from './Steps/Step2';
import RequestSuccess from './Success';
/**
 * ⚠️ Энэ дэлгэц өөрөө доорх файлын классуудыг бараг хэрэглэхээ больсон. Гэвч
 * LESS глобал тул `.map_comp_container`, `.merchant-request-*` зэрэг нь бусад
 * дэлгэцийн харагдацыг барьсаар байгаа тул импортыг устгаж болохгүй.
 */
import './request.less';

const { Text } = Typography;

const STEPS = [
  { title: 'Ерөнхий мэдээлэл', description: 'Бизнесийн бүртгэл' },
  { title: 'Салбарын мэдээлэл', description: 'Хаяг, байршил' },
];

/** `enum` + `enumDictionary` хосыг Select-ийн сонголт болгоно */
const toOptions = (field) =>
  (field?.enum ?? []).map((option) => ({
    value: option,
    label: field?.enumDictionary?.[option] ?? option,
  }));

const Request = (props) => {
  const [merchantRequests, token] = useStore((state) => [
    state?.user?.merchantRequests,
    state.token,
  ]);
  const history = useHistory();

  const [type, setType] = useState(null);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [affiliations, setAffiliations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchantAddress, setMerchantAddress] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [current, setCurrent] = useState(0);

  const goToStep = (step) => setCurrent(Math.min(Math.max(step, 0), STEPS.length - 1));
  const nextStep = () => goToStep(current + 1);
  const prevStep = () => goToStep(current - 1);

  /** Профайлын цэснээс "Бизнес нэмэх" гэж орсон эсэх */
  const isAddBusiness = props?.location?.state?.from === 'profileMenu';

  useEffect(() => {
    const fetchFields = async () => {
      setFieldsLoading(true);
      try {
        const { data } = await axios.get('/api/v1.0/merchant', {
          headers: { Authorization: token },
        });

        const affiliationOptions = toOptions(getObjectFromArray(data.result, 'affiliation'));
        if (affiliationOptions.length) setType(affiliationOptions[0].value);

        setAffiliations(affiliationOptions);
        setCategories(toOptions(getObjectFromArray(data.result, 'category')));
      } catch (e) {
        console.error('Fetch Merchant Fields: ', e);
      } finally {
        setFieldsLoading(false);
      }
    };
    void fetchFields();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (merchantRequests.length) setIsSuccessful(true);
    //eslint-disable-next-line
  }, []);

  const handleSubmit = async (values) => {
    // Зураг хуулах + хүсэлт илгээх нь удаан тул бүхэлд нь loading-оор хамарна
    setSubmitting(true);
    try {
      await submitRequest(values);
    } finally {
      setSubmitting(false);
    }
  };

  const submitRequest = async (values) => {
    const params = {
      ...values,
      affiliation: type,
      branchAddress: merchantAddress,
    };

    const attachment =
      type === 'C'
        ? [values.idCardFront[0].originFileObj, values.idCardBack[0].originFileObj]
        : [
            values.certificateFront[0].originFileObj,
            values?.certificateBack?.[0]?.originFileObj,
          ].filter((el) => el);

    const brandLogo = await imageUpload(values.brandLogo[0].originFileObj, 'merchantRequest');
    const att1 = await imageUpload(attachment[0], 'merchantRequest');
    const att2 =
      attachment?.length > 1 ? await imageUpload(attachment[1], 'merchantRequest') : null;

    params.brandLogo = brandLogo;
    delete params.idCardBack;
    delete params.idCardFront;
    delete params.certificateBack;
    delete params.certificateFront;
    params.attachment = [att1, att2].filter((el) => el);

    try {
      const { data } = await axios.post('/api/v1.0/merchant', params);
      if (data.statusCode === 200) {
        setIsSuccessful(true);
        if (isAddBusiness) history.push('/');
      }
    } catch (e) {
      console.error('Merchant Request Create: ', e);
    }
  };

  if (isSuccessful) {
    return <RequestSuccess />;
  }

  // Профайлын цэснээс орсон бол алхамгүй, нэг хуудсан дээрх богино хэлбэр
  if (isAddBusiness) {
    const AffiliationForm = type === 'C' ? CitizenForm : OrganizationForm;

    return (
      <div>
        <PPPageHeader title="Бизнес нэмэх" onBack={() => history.push('/')} />

        <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
          <SectionCard
            icon={<AiOutlineInfoCircle />}
            title="Бүртгэлийн мэдээлэл"
            /* Төрөл нь талбар биш, бүх формыг сольдог тул гарчгийн мөрөнд байрлана */
            action={
              <Space size={8}>
                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  Бүртгэлийн төрөл
                </Text>
                <Select
                  value={type}
                  onChange={setType}
                  options={affiliations}
                  loading={fieldsLoading}
                  style={{ width: 160 }}
                />
              </Space>
            }
          >
            <AffiliationForm
              onSubmit={handleSubmit}
              submitting={submitting}
              categories={categories}
              categoriesLoading={fieldsLoading}
              setMerchantAddress={setMerchantAddress}
            />
          </SectionCard>
        </Space>
      </div>
    );
  }

  const stepContent = [
    <Step1 nextStep={nextStep} />,
    <Step2 prevStep={prevStep} setIsSuccessful={setIsSuccessful} />,
  ];

  return (
    <div>
      <PPPageHeader
        title="Бүртгэлийн мэдээлэл"
        subTitle="Та дараах мэдээллийг бүртгүүлж бүртгэлээ баталгаажуулна уу."
      />

      <RegisterProvider>
        <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
          <Card size="middle">
            {/**
             * Гарчиг дээр дарж ӨМНӨХ алхам руу буцаж болно. Урагшаа алхам нь
             * disabled — мэдээллийг батлахгүйгээр цааш харайхыг хориглоно.
             */}
            <Steps
              current={current}
              responsive
              size="small"
              onChange={(step) => step < current && goToStep(step)}
              items={STEPS.map((step, index) => ({ ...step, disabled: index > current }))}
            />
          </Card>

          {stepContent[current]}
        </Space>
      </RegisterProvider>
    </div>
  );
};

export default Request;
